import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarIcon, Clock, Users, Plus, ChefHat, ChevronLeft, ChevronRight, X, Heart, CalendarPlus, Star, Flame, Beef, BookOpen, ExternalLink, Check, Trash2, MoveRight, Eye, Bell, ShoppingCart, ListPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { mockMealPlans } from "../data/mockData";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";
import {
  SwipeableMealModal, AddToCalendarModal, ScheduledMealModal,
  DayViewModal, GroceryModal, DueAlertModal
} from "../components/MealPlanModals";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const MealPlans = () => {
  const navigate = useNavigate();
  const [mealPlans, setMealPlans] = useState(mockMealPlans);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState(null); // 'breakfast', 'lunch', 'dinner'
  const [selectedDayIndex, setSelectedDayIndex] = useState(0); // Track which day was clicked
  const [currentMealIndex, setCurrentMealIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mealSuggestions, setMealSuggestions] = useState({
    breakfast: [],
    lunch: [],
    dinner: []
  });
  const [loadingMeals, setLoadingMeals] = useState(false);
  const [savedMeals, setSavedMeals] = useState(() => {
    // Load saved meals from localStorage
    const saved = localStorage.getItem('savedMeals');
    return saved ? JSON.parse(saved) : {};
  });
  const [mealImages, setMealImages] = useState(() => {
    // Load cached meal images from localStorage
    const cached = localStorage.getItem('mealImages');
    return cached ? JSON.parse(cached) : {};
  });
  const [generatingImage, setGeneratingImage] = useState(false);
  
  // Add to Calendar Modal State
  const [showAddToCalendarModal, setShowAddToCalendarModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [mealToAdd, setMealToAdd] = useState(null);
  const [addToCalendarSuccess, setAddToCalendarSuccess] = useState(false);
  
  // Scheduled Meal Management State
  const [showScheduledMealModal, setShowScheduledMealModal] = useState(false);
  const [selectedScheduledMeal, setSelectedScheduledMeal] = useState(null);
  const [scheduledMealKey, setScheduledMealKey] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTargetDate, setMoveTargetDate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Calendar Day View State
  const [showDayViewModal, setShowDayViewModal] = useState(false);
  const [selectedDayForView, setSelectedDayForView] = useState(null);

  // Grocery List Integration State
  const [showGroceryModal, setShowGroceryModal] = useState(false);
  const [groceryMeal, setGroceryMeal] = useState(null);
  const [groceryIngredients, setGroceryIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [groceryMode, setGroceryMode] = useState('now'); // 'now' or 'schedule'
  const [groceryScheduleDate, setGroceryScheduleDate] = useState(null);
  const [groceryScheduleMode, setGroceryScheduleMode] = useState('alert'); // 'alert' or 'auto'
  const [grocerySuccess, setGrocerySuccess] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null); // Which grocery list to add to
  
  // Due Schedule Alert State
  const [dueSchedules, setDueSchedules] = useState([]);
  const [showDueAlert, setShowDueAlert] = useState(false);
  const [currentDueIndex, setCurrentDueIndex] = useState(0);
  
  // Favorites State
  const [favoriteMeals, setFavoriteMeals] = useState({}); // { mealName: fav_id }
  
  // Access grocery list context
  const { lists, currentList, currentListId, setLists, updateCurrentList } = useListContext();
  const { user } = useAuth();

  const weekDays = [
    "Monday", "Tuesday", "Wednesday", "Thursday", 
    "Friday", "Saturday", "Sunday"
  ];

  // State to store meals for each day (indexed by day)
  const [mealsByDay, setMealsByDay] = useState({});

  // Function to generate and cache meal image
  const generateMealImage = async (mealName, mealType) => {
    // Check if image is already cached
    if (mealImages[mealName]) {
      return mealImages[mealName];
    }

    // Check if we're already generating this image (prevent duplicates)
    if (window.generatingImages && window.generatingImages[mealName]) {
      return null;
    }

    // Mark this image as being generated
    if (!window.generatingImages) {
      window.generatingImages = {};
    }
    window.generatingImages[mealName] = true;

    try {
      console.log(`🎨 Generating AI image for: ${mealName}`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
      
      const response = await fetch(
        `${BACKEND_URL}/api/images/generate-meal-image?meal_name=${encodeURIComponent(mealName)}&meal_type=${mealType}`,
        { 
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.image;
        
        console.log(`✅ Image generated for: ${mealName}`);
        
        // Cache the image and trigger re-render
        setMealImages(prevImages => {
          const updatedImages = { ...prevImages, [mealName]: imageUrl };
          localStorage.setItem('mealImages', JSON.stringify(updatedImages));
          return updatedImages;
        });
        
        return imageUrl;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error(`⏱️ Image generation timeout for ${mealName}`);
      } else {
        console.error(`❌ Failed to generate image for ${mealName}:`, error);
      }
    } finally {
      // Remove from generating list
      if (window.generatingImages) {
        delete window.generatingImages[mealName];
      }
    }
    
    return null;
  };

  // Get image for a meal - USE SPOONACULAR IMAGE FROM RECIPE DATA
  const getMealImage = (meal) => {
    if (meal && meal.image) return meal.image;
    return "";
  };

  // ── MealImage component ──────────────────────────────────────────────
  // Renders the recipe's real photo. If the image URL fails to load (broken
  // CDN, expired signature, network hiccup) OR no image was provided, swaps
  // in a small "We're working on it — refresh to retry" text card so users
  // never confuse a stock photo with the actual recipe photo.
  const MealImage = ({ meal, sizeClass }) => {
    const [errored, setErrored] = useState(false);
    const src = getMealImage(meal);

    if (!src || errored) {
      return (
        <div
          className={`${sizeClass} rounded-lg flex flex-col items-center justify-center text-center bg-gray-100 border border-dashed border-gray-300 text-gray-500 px-1`}
          data-testid="meal-image-fallback"
        >
          <span className="text-[9px] leading-tight font-medium">Working on it</span>
          <span className="text-[8px] leading-tight">Refresh</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={meal?.name || "meal"}
        className={`${sizeClass} rounded-lg object-cover`}
        onError={() => setErrored(true)}
      />
    );
  };

  // Fetch meal suggestions from API for ALL 7 days
  useEffect(() => {
    const fetchMealSuggestionsForAllDays = async () => {
      setLoadingMeals(true);
      try {
        const dietParts = [];
        if (user?.dietary_preferences?.length) dietParts.push(...user.dietary_preferences);
        if (user?.allergies?.length) dietParts.push(...user.allergies.map(a => `no-${a.toLowerCase()}`));
        const dietParam = dietParts.length > 0 ? `&dietary_restrictions=${encodeURIComponent(dietParts.join(","))}` : "";

        // Single bulk request instead of 42 individual calls
        const res = await fetch(`${BACKEND_URL}/api/meals/suggestions/bulk?days=14&count=10${dietParam}`);
        if (res.ok) {
          const data = await res.json();
          const mealsByDayTemp = {};
          for (let i = 0; i < 14; i++) {
            const dayData = data.meals_by_day[String(i)];
            if (dayData) {
              mealsByDayTemp[i] = dayData;
            }
          }
          setMealsByDay(mealsByDayTemp);
          if (mealsByDayTemp[0]) {
            setMealSuggestions(mealsByDayTemp[0]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch meal suggestions:", error);
      } finally {
        setLoadingMeals(false);
      }
    };

    fetchMealSuggestionsForAllDays();
  }, []); // Fetch once on component mount

  // No longer needed - Spoonacular provides images with each recipe!

  // Get meal for a specific day and meal type
  const getMealForDay = (dayIndex, mealType) => {
    // Check if there's a saved meal for this day and meal type
    const savedMealKey = `${currentWeekOffset}-${weekDays[dayIndex]}-${mealType}`;
    const legacySavedKey = `${weekDays[dayIndex]}-${mealType}`;
    if (savedMeals[savedMealKey]) {
      return savedMeals[savedMealKey];
    }
    if (savedMeals[legacySavedKey] && currentWeekOffset === 0) {
      return savedMeals[legacySavedKey];
    }
    
    // Use week offset to get different meals: week 0 = offsets 0-6, week 1 = offsets 7-13
    const absoluteOffset = dayIndex + (currentWeekOffset * 7);
    const dayMeals = mealsByDay[absoluteOffset];
    if (!dayMeals || !dayMeals[mealType] || dayMeals[mealType].length === 0) return null;
    
    // Return the first meal for display (user can browse all 10 in the modal)
    return dayMeals[mealType][0];
  };

  // Get all 10 meal suggestions for a specific day and meal type (for modal)
  const getMealSuggestionsForDay = (dayIndex, mealType) => {
    const absoluteOffset = dayIndex + (currentWeekOffset * 7);
    const dayMeals = mealsByDay[absoluteOffset];
    if (!dayMeals || !dayMeals[mealType]) return [];
    return dayMeals[mealType];
  };

  // Get current week start date
  const getCurrentWeekStart = (offset = 0) => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calculate Monday of current week
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset + (offset * 7));
    return monday;
  };

  // Format date range for display
  const getWeekDateRange = (weekStart) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    const year = weekStart.getFullYear();
    return `${formatDate(weekStart)} - ${formatDate(weekEnd)}, ${year}`;
  };

  // Get week label
  const getWeekLabel = (offset) => {
    if (offset === 0) return "This Week";
    if (offset === 1) return "Next Week";
    return "This Week"; // Fallback
  };

  // Navigate weeks - limited to current week (0) and next week (1)
  const navigateWeek = (direction) => {
    const newOffset = currentWeekOffset + direction;
    // Limit to 0 (this week) and 1 (next week) only
    if (newOffset >= 0 && newOffset <= 1) {
      setCurrentWeekOffset(newOffset);
    }
  };

  // Generate calendar days for the selected week
  const generateCalendarDays = () => {
    const weekStart = getCurrentWeekStart(currentWeekOffset);
    const days = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      days.push({
        date: date,
        dayName: weekDays[i],
        dayNumber: date.getDate(),
        isToday: date.toDateString() === new Date().toDateString(),
        isPast: date < new Date().setHours(0,0,0,0),
        isFuture: date > new Date().setHours(23,59,59,999)
      });
    }
    
    return days;
  };

  const currentWeekStart = getCurrentWeekStart(currentWeekOffset);
  const calendarDays = generateCalendarDays();

  // Touch gesture detection for swiping
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleSwipeLeft(); // Not interested
    } else if (isRightSwipe) {
      handleSwipeRight(); // Add to calendar options
    }
  };

  const handleSwipeLeft = () => {
    // Not interested - go to next meal
    const meals = getMealSuggestionsForDay(selectedDayIndex, selectedMealType);
    if (currentMealIndex < meals.length - 1) {
      setCurrentMealIndex(currentMealIndex + 1);
    } else {
      setCurrentMealIndex(0); // Loop back to start
    }
  };

  const handleSwipeRight = () => {
    // Open the Add to Calendar modal instead of directly saving
    const meals = getMealSuggestionsForDay(selectedDayIndex, selectedMealType);
    const currentMeal = meals[currentMealIndex];
    
    // Set the meal to add and open the calendar modal
    setMealToAdd(currentMeal);
    
    // Default to the currently viewed day
    const weekStart = getCurrentWeekStart(currentWeekOffset);
    const selectedDate = new Date(weekStart);
    selectedDate.setDate(weekStart.getDate() + selectedDayIndex);
    setSelectedCalendarDate(selectedDate);
    
    setShowAddToCalendarModal(true);
  };

  const handleAddToCalendarConfirm = () => {
    if (!mealToAdd || !selectedCalendarDate) return;
    
    // Format date key for saving (YYYY-MM-DD format for uniqueness)
    const dateKey = selectedCalendarDate.toISOString().split('T')[0];
    const savedMealKey = `${dateKey}-${selectedMealType}`;
    
    // Also create a readable key for display
    const dayName = selectedCalendarDate.toLocaleDateString('en-US', { weekday: 'long' });
    const formattedDate = selectedCalendarDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    // Save to state and localStorage
    const updatedSavedMeals = {
      ...savedMeals,
      [savedMealKey]: {
        ...mealToAdd,
        scheduledDate: dateKey,
        scheduledDayName: dayName,
        scheduledMealType: selectedMealType
      }
    };
    setSavedMeals(updatedSavedMeals);
    localStorage.setItem('savedMeals', JSON.stringify(updatedSavedMeals));
    
    // Show success state
    setAddToCalendarSuccess(true);
    
    // Log success
    console.log(`✅ Added "${mealToAdd.name}" to ${dayName}, ${formattedDate} (${selectedMealType})`);
    
    // Close modal after a brief delay to show success message
    setTimeout(() => {
      const addedMeal = mealToAdd; // Capture before clearing
      setShowAddToCalendarModal(false);
      setAddToCalendarSuccess(false);
      setMealToAdd(null);
      setSelectedCalendarDate(null);
      
      // Move to next meal after selection
      const meals = getMealSuggestionsForDay(selectedDayIndex, selectedMealType);
      if (currentMealIndex < meals.length - 1) {
        setCurrentMealIndex(currentMealIndex + 1);
      } else {
        setCurrentMealIndex(0);
      }
      
      // Open grocery modal if meal has ingredients
      if (addedMeal && addedMeal.base_ingredients && addedMeal.base_ingredients.length > 0) {
        openGroceryModal(addedMeal);
      }
    }, 1500);
  };

  const closeAddToCalendarModal = () => {
    setShowAddToCalendarModal(false);
    setAddToCalendarSuccess(false);
    setMealToAdd(null);
    setSelectedCalendarDate(null);
  };

  // Scheduled Meal Management Functions
  const openScheduledMealModal = (mealKey, meal) => {
    setScheduledMealKey(mealKey);
    setSelectedScheduledMeal(meal);
    setShowScheduledMealModal(true);
  };

  const closeScheduledMealModal = () => {
    setShowScheduledMealModal(false);
    setSelectedScheduledMeal(null);
    setScheduledMealKey(null);
    setShowDeleteConfirm(false);
    setShowMoveModal(false);
    setMoveTargetDate(null);
  };

  const handleDeleteScheduledMeal = () => {
    if (!scheduledMealKey) return;
    
    const updatedSavedMeals = { ...savedMeals };
    delete updatedSavedMeals[scheduledMealKey];
    
    setSavedMeals(updatedSavedMeals);
    localStorage.setItem('savedMeals', JSON.stringify(updatedSavedMeals));
    
    console.log(`🗑️ Deleted meal from ${scheduledMealKey}`);
    closeScheduledMealModal();
  };

  const handleMoveScheduledMeal = () => {
    if (!scheduledMealKey || !moveTargetDate || !selectedScheduledMeal) return;
    
    // Get the meal type from the original key
    const mealType = scheduledMealKey.split('-').pop();
    
    // Create new key for the target date
    const newDateKey = moveTargetDate.toISOString().split('T')[0];
    const newMealKey = `${newDateKey}-${mealType}`;
    
    // Get day name for the new date
    const dayName = moveTargetDate.toLocaleDateString('en-US', { weekday: 'long' });
    
    // Create updated meals object
    const updatedSavedMeals = { ...savedMeals };
    
    // Remove from old date
    delete updatedSavedMeals[scheduledMealKey];
    
    // Add to new date
    updatedSavedMeals[newMealKey] = {
      ...selectedScheduledMeal,
      scheduledDate: newDateKey,
      scheduledDayName: dayName,
      scheduledMealType: mealType
    };
    
    setSavedMeals(updatedSavedMeals);
    localStorage.setItem('savedMeals', JSON.stringify(updatedSavedMeals));
    
    console.log(`📅 Moved meal from ${scheduledMealKey} to ${newMealKey}`);
    closeScheduledMealModal();
  };

  // Calendar Day View Functions
  const openDayViewModal = (day) => {
    setSelectedDayForView(day);
    setShowDayViewModal(true);
  };

  const closeDayViewModal = () => {
    setShowDayViewModal(false);
    setSelectedDayForView(null);
  };

  const openMealModal = async (mealType, dayIndex) => {
    setSelectedMealType(mealType);
    setSelectedDayIndex(dayIndex);
    setCurrentMealIndex(0);
    // No image generation needed - Spoonacular provides images!
  };

  const closeMealModal = () => {
    setSelectedMealType(null);
    setSelectedDayIndex(0);
    setCurrentMealIndex(0);
  };

  const navigateToNextMeal = () => {
    const meals = getMealSuggestionsForDay(selectedDayIndex, selectedMealType);
    if (currentMealIndex < meals.length - 1) {
      setCurrentMealIndex(currentMealIndex + 1);
    } else {
      setCurrentMealIndex(0);
    }
  };

  const navigateToPrevMeal = () => {
    const meals = getMealSuggestionsForDay(selectedDayIndex, selectedMealType);
    if (currentMealIndex > 0) {
      setCurrentMealIndex(currentMealIndex - 1);
    } else {
      setCurrentMealIndex(meals.length - 1);
    }
  };

  // === GROCERY LIST INTEGRATION ===

  // Auto-categorize ingredient for grocery list
  const categorizeIngredient = (name) => {
    const n = name.toLowerCase();
    const produce = ["lettuce", "tomato", "onion", "garlic", "pepper", "carrot", "broccoli", "spinach", "celery", "cucumber", "potato", "avocado", "lemon", "lime", "basil", "cilantro", "parsley", "ginger", "mushroom", "zucchini", "corn", "peas", "asparagus", "kale", "cabbage", "cauliflower", "eggplant", "beet", "radish", "squash", "apple", "banana", "berry", "mango", "orange"];
    const meat = ["chicken", "beef", "pork", "turkey", "lamb", "steak", "bacon", "sausage", "ham", "salmon", "tuna", "shrimp", "fish", "cod", "tilapia", "crab", "scallop"];
    const dairy = ["milk", "cheese", "yogurt", "butter", "cream", "egg", "mozzarella", "parmesan", "cheddar", "feta", "ricotta", "sour cream"];
    const pantry = ["rice", "pasta", "flour", "sugar", "salt", "oil", "vinegar", "sauce", "broth", "stock", "beans", "lentils", "chickpea", "oat", "quinoa", "noodle", "bread", "tortilla"];
    if (produce.some(p => n.includes(p))) return "Produce";
    if (meat.some(p => n.includes(p))) return "Meat & Seafood";
    if (dairy.some(p => n.includes(p))) return "Dairy & Eggs";
    if (pantry.some(p => n.includes(p))) return "Pantry";
    return "Other";
  };

  // Trigger notification sound using device alert
  const playNotificationSound = useCallback(() => {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Grocery Reminder', { 
          body: 'You have ingredients to add to your grocery list!',
          icon: '/favicon.ico',
          silent: false
        });
      } else {
        // Fallback: Web Audio API beep
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.value = 0.3;
        osc.start();
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
        osc.stop(ctx.currentTime + 0.5);
      }
    } catch (e) {
      console.log('Could not play notification sound');
    }
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch meal-plan favorites on mount
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/recipes/favorites`);
        if (res.ok) {
          const data = await res.json();
          const mealFavs = {};
          (data.favorites || []).filter(f => f.source === "meal-plan").forEach(f => {
            if (f.recipe?.name) mealFavs[f.recipe.name] = f.fav_id;
          });
          setFavoriteMeals(mealFavs);
        }
      } catch (e) { /* ignore */ }
    };
    fetchFavorites();
  }, []);

  // Toggle favorite for a meal
  const toggleFavoriteMeal = async (meal) => {
    if (!meal) return;
    const existingFavId = favoriteMeals[meal.name];
    
    if (existingFavId) {
      // Unfavorite
      try {
        const res = await fetch(`${BACKEND_URL}/api/recipes/favorites/${existingFavId}`, { method: "DELETE" });
        if (res.ok) {
          setFavoriteMeals(prev => {
            const next = { ...prev };
            delete next[meal.name];
            return next;
          });
        }
      } catch (e) { /* ignore */ }
    } else {
      // Favorite
      try {
        const res = await fetch(`${BACKEND_URL}/api/recipes/favorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            recipe: {
              name: meal.name,
              image: getMealImage(meal),
              prep_time: meal.prep_time || "",
              cook_time: meal.cook_time || "",
              calories: meal.calories || 0,
              protein_g: meal.protein_g || 0,
              carbs_g: meal.carbs_g || 0,
              fat_g: meal.fat_g || 0,
              difficulty: meal.difficulty || "",
              servings: meal.servings || 0,
              base_ingredients: meal.base_ingredients || [],
              ingredients: (meal.base_ingredients || []).map(name => ({ name, amount: "", have: false })),
              source_url: meal.source_url || meal.spoonacular_url || "",
              category: meal.meal_type || "dinner",
            },
            source: "meal-plan",
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setFavoriteMeals(prev => ({ ...prev, [meal.name]: data.fav_id }));
        }
      } catch (e) { /* ignore */ }
    }
  };

  // Check for due grocery schedules on mount
  useEffect(() => {
    const checkDueSchedules = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/grocery/due-schedules`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.schedules && data.schedules.length > 0) {
          // Separate auto and alert schedules
          const autoSchedules = data.schedules.filter(s => s.mode === 'auto');
          const alertSchedules = data.schedules.filter(s => s.mode === 'alert');

          // Auto-add ingredients immediately
          for (const schedule of autoSchedules) {
            addIngredientsToGroceryList(schedule.ingredients);
            // Mark as processed
            await fetch(`${BACKEND_URL}/api/grocery/schedules/${schedule.schedule_id}/process`, { method: 'POST' });
          }

          // Show alert for alert-mode schedules
          if (alertSchedules.length > 0) {
            setDueSchedules(alertSchedules);
            setCurrentDueIndex(0);
            setShowDueAlert(true);
            playNotificationSound();
          }
        }
      } catch (e) {
        console.error('Failed to check due schedules:', e);
      }
    };
    checkDueSchedules();
  }, []);

  // Add ingredients directly to grocery list
  // Title-case ingredient name to match grocery list style (e.g., "parmesan cheese" → "Parmesan Cheese")
  const titleCase = (str) => str.replace(/\b\w/g, c => c.toUpperCase());

  const addIngredientsToGroceryList = (ingredientNames) => {
    const targetListId = selectedListId || currentListId;
    const targetList = lists.find(l => l.id === targetListId);
    if (!targetList) return 0;
    
    const groceryItems = targetList.items || [];
    const existingNames = new Set(groceryItems.map(i => i.name.toLowerCase()));
    
    const newItems = ingredientNames
      .filter(name => !existingNames.has(name.toLowerCase()))
      .map((name, idx) => ({
        id: Date.now() + Math.random() + idx,
        name: titleCase(name),
        quantity: 1,
        completed: false,
        category: categorizeIngredient(name),
        estimated_price: 2.99,
        source: 'meal-plan'
      }));
    
    if (newItems.length > 0) {
      setLists(prev => prev.map(list =>
        list.id === targetListId
          ? { ...list, items: [...newItems, ...list.items] }
          : list
      ));
    }
    return newItems.length;
  };

  // Open grocery modal after calendar add
  // Loading state for ingredient fetch
  const [loadingIngredients, setLoadingIngredients] = useState(false);

  const openGroceryModal = async (meal) => {
    setGroceryMeal(meal);
    setGroceryMode('now');
    setGroceryScheduleDate(null);
    setGroceryScheduleMode('alert');
    setGrocerySuccess(false);
    setSelectedListId(currentListId); // Default to current list
    setShowGroceryModal(true);
    
    // Start with existing base_ingredients while we fetch full list
    const fallbackIngredients = meal.base_ingredients || [];
    setGroceryIngredients(fallbackIngredients);
    setSelectedIngredients([...fallbackIngredients]);
    
    // Fetch full ingredient list from backend
    if (meal.id) {
      setLoadingIngredients(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/meals/recipe/${meal.id}/ingredients`);
        if (res.ok) {
          const data = await res.json();
          if (data.ingredients && data.ingredients.length > 0) {
            setGroceryIngredients(data.ingredients);
            setSelectedIngredients([...data.ingredients]);
          }
        }
      } catch (e) {
        console.error('Failed to fetch full ingredients:', e);
      } finally {
        setLoadingIngredients(false);
      }
    }
  };

  const closeGroceryModal = () => {
    setShowGroceryModal(false);
    setGroceryMeal(null);
    setGroceryIngredients([]);
    setSelectedIngredients([]);
    setGrocerySuccess(false);
  };

  const toggleIngredient = (ingredient) => {
    setSelectedIngredients(prev => 
      prev.includes(ingredient) 
        ? prev.filter(i => i !== ingredient)
        : [...prev, ingredient]
    );
  };

  const handleGroceryConfirm = async () => {
    if (selectedIngredients.length === 0) return;

    if (groceryMode === 'now') {
      // Add directly to grocery list
      const added = addIngredientsToGroceryList(selectedIngredients);
      setGrocerySuccess(true);
      setTimeout(() => closeGroceryModal(), 1500);
    } else {
      // Schedule for later
      if (!groceryScheduleDate) return;
      try {
        const dateKey = groceryScheduleDate.toISOString().split('T')[0];
        const res = await fetch(`${BACKEND_URL}/api/grocery/schedule`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            meal_name: groceryMeal.name,
            meal_type: selectedMealType || 'dinner',
            ingredients: selectedIngredients,
            target_date: dateKey,
            mode: groceryScheduleMode
          })
        });
        if (res.ok) {
          setGrocerySuccess(true);
          setTimeout(() => closeGroceryModal(), 1500);
        }
      } catch (e) {
        console.error('Failed to schedule grocery addition:', e);
      }
    }
  };

  // Handle due alert actions
  const handleDueAlertAdd = async () => {
    const schedule = dueSchedules[currentDueIndex];
    if (!schedule) return;
    
    addIngredientsToGroceryList(schedule.ingredients);
    await fetch(`${BACKEND_URL}/api/grocery/schedules/${schedule.schedule_id}/process`, { method: 'POST' });
    
    if (currentDueIndex < dueSchedules.length - 1) {
      setCurrentDueIndex(currentDueIndex + 1);
      playNotificationSound();
    } else {
      setShowDueAlert(false);
    }
  };

  const handleDueAlertDismiss = async () => {
    const schedule = dueSchedules[currentDueIndex];
    if (!schedule) return;
    
    await fetch(`${BACKEND_URL}/api/grocery/schedules/${schedule.schedule_id}/process`, { method: 'POST' });
    
    if (currentDueIndex < dueSchedules.length - 1) {
      setCurrentDueIndex(currentDueIndex + 1);
    } else {
      setShowDueAlert(false);
    }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Meal Plans</h2>
        <p className="text-gray-600">Plan your weekly meals</p>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigateWeek(-1)}
              disabled={currentWeekOffset <= 0}
              className={`flex items-center gap-2 ${currentWeekOffset <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <ChevronLeft className="h-4 w-4" />
              Current Week
            </Button>
            <div className="text-center">
              <p className="font-semibold">{getWeekLabel(currentWeekOffset)}</p>
              <p className="text-sm text-gray-500">{getWeekDateRange(currentWeekStart)}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigateWeek(1)}
              disabled={currentWeekOffset >= 1}
              className={`flex items-center gap-2 ${currentWeekOffset >= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next Week
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Calendar Toggle Button */}
          <div className="flex justify-center mt-3 pt-3 border-t">
            <Button
              variant={showCalendar ? "default" : "outline"}
              size="sm"
              onClick={() => setShowCalendar(!showCalendar)}
              className={`flex items-center gap-2 ${showCalendar ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-blue-600 border-blue-300 hover:bg-blue-50'}`}
            >
              <CalendarIcon className="h-4 w-4" />
              {showCalendar ? 'Hide Week Calendar' : 'Show Week Calendar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar View - Toggle controlled */}
      {showCalendar && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Week Calendar
              <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">
                {currentWeekOffset === 0 ? 'This Week' : 'Next Week'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map((day, index) => {
                // Check for scheduled meals on this day
                const dateKey = day.date.toISOString().split('T')[0];
                const scheduledBreakfast = savedMeals[`${dateKey}-breakfast`];
                const scheduledLunch = savedMeals[`${dateKey}-lunch`];
                const scheduledDinner = savedMeals[`${dateKey}-dinner`];
                const hasScheduledMeals = scheduledBreakfast || scheduledLunch || scheduledDinner;
                
                return (
                  <div
                    key={index}
                    onClick={() => openDayViewModal({ ...day, dateKey })}
                    className={`p-3 rounded-lg text-center border transition-all cursor-pointer ${
                      day.isToday 
                        ? hasScheduledMeals
                          ? "bg-blue-500 text-white border-blue-600 shadow-md ring-2 ring-green-400 ring-offset-1"
                          : "bg-blue-500 text-white border-blue-600 shadow-md"
                        : day.isPast 
                        ? "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200" 
                        : hasScheduledMeals
                        ? "bg-green-50 text-gray-700 border-green-300 hover:bg-green-100"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-blue-50 hover:border-blue-300"
                    }`}
                  >
                    <div className="text-xs font-medium mb-1">
                      {day.dayName.slice(0, 3)}
                    </div>
                    <div className={`text-lg font-bold ${day.isToday ? "text-white" : ""}`}>
                      {day.dayNumber}
                    </div>
                    {day.isToday && (
                      <div className="text-xs text-blue-100 mt-1">Today</div>
                    )}
                    {/* Show scheduled meal indicators - now shows for Today too */}
                    {hasScheduledMeals && (
                      <div className="flex justify-center gap-0.5 mt-1">
                        {scheduledBreakfast && (
                          <div 
                            className={`w-2 h-2 rounded-full ${day.isToday ? 'bg-orange-300 ring-1 ring-white' : 'bg-orange-400'}`} 
                            title="Breakfast"
                          ></div>
                        )}
                        {scheduledLunch && (
                          <div 
                            className={`w-2 h-2 rounded-full ${day.isToday ? 'bg-green-300 ring-1 ring-white' : 'bg-green-500'}`} 
                            title="Lunch"
                          ></div>
                        )}
                        {scheduledDinner && (
                          <div 
                            className={`w-2 h-2 rounded-full ${day.isToday ? 'bg-purple-300 ring-1 ring-white' : 'bg-purple-500'}`} 
                            title="Dinner"
                          ></div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Scheduled Meals for the Week */}
            {(() => {
              const scheduledMealsThisWeek = calendarDays.filter(day => {
                const dateKey = day.date.toISOString().split('T')[0];
                return savedMeals[`${dateKey}-breakfast`] || savedMeals[`${dateKey}-lunch`] || savedMeals[`${dateKey}-dinner`];
              });
              
              if (scheduledMealsThisWeek.length > 0) {
                return (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <CalendarPlus className="h-4 w-4" />
                      Scheduled Meals This Week
                      <span className="text-xs font-normal text-gray-500">(Click to manage)</span>
                    </h4>
                    <div className="space-y-2">
                      {scheduledMealsThisWeek.map((day, idx) => {
                        const dateKey = day.date.toISOString().split('T')[0];
                        const meals = [
                          { type: 'breakfast', meal: savedMeals[`${dateKey}-breakfast`], color: 'orange', key: `${dateKey}-breakfast` },
                          { type: 'lunch', meal: savedMeals[`${dateKey}-lunch`], color: 'green', key: `${dateKey}-lunch` },
                          { type: 'dinner', meal: savedMeals[`${dateKey}-dinner`], color: 'purple', key: `${dateKey}-dinner` }
                        ].filter(m => m.meal);
                        
                        return (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-blue-100">
                            <div className="text-sm font-medium text-gray-800 mb-2">
                              {day.dayName}, {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {day.isToday && <Badge className="ml-2 bg-blue-500 text-white text-xs">Today</Badge>}
                            </div>
                            <div className="space-y-2">
                              {meals.map((m, mIdx) => (
                                <div 
                                  key={mIdx} 
                                  onClick={() => openScheduledMealModal(m.key, m.meal)}
                                  className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group border border-transparent hover:border-gray-200"
                                  data-testid={`scheduled-meal-row-${m.type}`}
                                >
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    m.color === 'orange' ? 'bg-orange-400' : 
                                    m.color === 'green' ? 'bg-green-500' : 'bg-purple-500'
                                  }`}></div>
                                  <span className="capitalize text-gray-500 w-16 flex-shrink-0">{m.type}:</span>
                                  <span className="text-gray-800 truncate flex-1">{m.meal.name}</span>
                                  <span className="text-xs text-gray-400 flex-shrink-0">{m.meal.calories} cal</span>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Eye className="h-3.5 w-3.5 text-blue-500" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Calendar Legend */}
            <div className="flex justify-center gap-4 mt-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-600">Today</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-50 border border-green-300 rounded"></div>
                <span className="text-gray-600">Scheduled</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
                <span className="text-gray-600">Past</span>
              </div>
            </div>
            
            {/* Meal Type Legend */}
            <div className="flex justify-center gap-4 mt-2 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span className="text-gray-600">Breakfast</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Lunch</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">Dinner</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Meal Plans */}
      <div className="space-y-4">
        {calendarDays.map((day, dayIndex) => {
          const dayMeal = mealPlans.find(meal => meal.day === day.dayName);
          
          // Get unique meals for this specific day
          const breakfastMeal = getMealForDay(dayIndex, 'breakfast');
          const lunchMeal = getMealForDay(dayIndex, 'lunch');
          const dinnerMeal = getMealForDay(dayIndex, 'dinner');

          // Has the user set any dietary restrictions? (Used to decide whether to show
          // the "no meal available for your diet" placeholder vs the generic "No meals planned".)
          const hasDietaryRestrictions =
            (user?.dietary_preferences?.length || 0) + (user?.allergies?.length || 0) > 0;

          // Has the bulk fetch completed for this specific day? If the day's entry exists
          // in mealsByDay, the fetch has returned (possibly empty). Otherwise we're still loading.
          const absoluteOffset = dayIndex + (currentWeekOffset * 7);
          const dayFetchComplete = !!mealsByDay[absoluteOffset];

          // Per-slot placeholder shown when a meal slot ends up empty —
          // either because the user's strict diet+allergen combo returned
          // 0 matches, OR because every recipe in that slot was filtered
          // out due to missing/broken images. The backend cache will warm
          // up over time; in the meantime we give the user a calm message.
          const DietUnavailableCard = ({ mealType, accentColor, bgColor }) => (
            <button
              data-testid={`diet-unavailable-${mealType}`}
              onClick={() => navigate("/settings")}
              className={`w-full text-left flex items-start gap-3 p-3 rounded-lg ${bgColor} border border-dashed hover:border-solid hover:shadow-sm transition-all active:scale-[0.99]`}
            >
              <div className={`w-2 h-8 ${accentColor} rounded flex-shrink-0`}></div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 capitalize">{mealType}</p>
                <p className="text-xs text-gray-600 mt-0.5 leading-snug">
                  Hang tight or come back soon while we work on your personalized meal plans for this period.
                </p>
              </div>
            </button>
          );
          
          return (
            <Card key={day.dayName} className={`bg-white ${day.isPast ? "opacity-75" : ""}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {day.dayName}
                    <span className="text-sm font-normal text-gray-500">
                      {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </CardTitle>
                  <div className="flex gap-2">
                    {day.isToday && (
                      <Badge className="bg-blue-500 text-white">Today</Badge>
                    )}
                    {day.isPast && (
                      <Badge variant="outline" className="text-gray-500 border-gray-300">Past</Badge>
                    )}
                    {day.isFuture && (
                      <Badge variant="outline" className="text-green-600 border-green-300">Upcoming</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {!dayFetchComplete ? (
                  // Still loading — show a subtle skeleton/placeholder
                  <div className="text-center py-8" data-testid={`meals-loading-${dayIndex}`}>
                    <ChefHat className="h-8 w-8 text-gray-300 mx-auto mb-2 animate-pulse" />
                    <p className="text-gray-400 text-sm">Loading meal suggestions…</p>
                  </div>
                ) : (
                  <>
                    {/* Breakfast */}
                    {breakfastMeal ? (
                      <div 
                        className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
                        onClick={() => openMealModal('breakfast', dayIndex)}
                      >
                        <div className="w-2 h-8 bg-orange-500 rounded"></div>
                        <MealImage meal={breakfastMeal} sizeClass="w-12 h-12" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Breakfast</p>
                          <p className="text-sm text-gray-600">{breakfastMeal.name}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {breakfastMeal.prep_time}
                          </p>
                          <p className="flex items-center gap-1 text-orange-600 font-semibold">
                            <Flame className="h-3 w-3" />
                            {breakfastMeal.calories} cal
                          </p>
                        </div>
                      </div>
                    ) : (
                      <DietUnavailableCard mealType="breakfast" accentColor="bg-orange-500" bgColor="bg-orange-50" />
                    )}

                    {/* Lunch */}
                    {lunchMeal ? (
                      <div 
                        className="flex items-center gap-3 p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                        onClick={() => openMealModal('lunch', dayIndex)}
                      >
                        <div className="w-2 h-8 bg-green-500 rounded"></div>
                        <MealImage meal={lunchMeal} sizeClass="w-12 h-12" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Lunch</p>
                          <p className="text-sm text-gray-600">{lunchMeal.name}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {lunchMeal.prep_time}
                          </p>
                          <p className="flex items-center gap-1 text-green-600 font-semibold">
                            <Flame className="h-3 w-3" />
                            {lunchMeal.calories} cal
                          </p>
                        </div>
                      </div>
                    ) : (
                      <DietUnavailableCard mealType="lunch" accentColor="bg-green-500" bgColor="bg-green-50" />
                    )}

                    {/* Dinner */}
                    {dinnerMeal ? (
                      <div 
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => openMealModal('dinner', dayIndex)}
                      >
                        <div className="w-2 h-8 bg-gray-600 rounded"></div>
                        <MealImage meal={dinnerMeal} sizeClass="w-12 h-12" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">Dinner</p>
                          <p className="text-sm text-gray-600">{dinnerMeal.name}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {dinnerMeal.prep_time}
                          </p>
                          <p className="flex items-center gap-1 text-gray-700 font-semibold">
                            <Flame className="h-3 w-3" />
                            {dinnerMeal.calories} cal
                          </p>
                        </div>
                      </div>
                    ) : (
                      <DietUnavailableCard mealType="dinner" accentColor="bg-gray-600" bgColor="bg-gray-50" />
                    )}

                    {/* No meals AND no dietary restrictions: show generic empty state */}
                    {!breakfastMeal && !lunchMeal && !dinnerMeal && !hasDietaryRestrictions && (
                      <div className="text-center py-8 hidden">
                        <ChefHat className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">No meals planned</p>
                        <Button variant="ghost" className="mt-2 text-green-600">
                          Add meals for {day.dayName}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ─────────── Extracted Modals ─────────── */}
      <SwipeableMealModal ctx={{
        selectedMealType, selectedDayIndex, currentMealIndex,
        getMealSuggestionsForDay, weekDays, closeMealModal,
        onTouchStart, onTouchMove, onTouchEnd, getMealImage,
        favoriteMeals, toggleFavoriteMeal,
        navigateToPrevMeal, navigateToNextMeal,
        handleSwipeLeft, handleSwipeRight
      }} />
      <AddToCalendarModal ctx={{
        showAddToCalendarModal, mealToAdd, closeAddToCalendarModal,
        addToCalendarSuccess, selectedCalendarDate, setSelectedCalendarDate,
        handleAddToCalendarConfirm, selectedMealType, getMealImage
      }} />
      <ScheduledMealModal ctx={{
        showScheduledMealModal, selectedScheduledMeal, closeScheduledMealModal,
        scheduledMealKey, showMoveModal, setShowMoveModal, moveTargetDate,
        setMoveTargetDate, handleMoveScheduledMeal, showDeleteConfirm,
        setShowDeleteConfirm, handleDeleteScheduledMeal, getMealImage, openGroceryModal
      }} />
      <DayViewModal ctx={{
        showDayViewModal, selectedDayForView, closeDayViewModal,
        savedMeals, getMealImage, openScheduledMealModal
      }} />
      <GroceryModal ctx={{
        showGroceryModal, groceryMeal, closeGroceryModal, grocerySuccess,
        groceryMode, setGroceryMode, selectedIngredients, setSelectedIngredients,
        groceryIngredients, loadingIngredients, toggleIngredient, categorizeIngredient,
        selectedListId, setSelectedListId, lists, currentList,
        groceryScheduleMode, setGroceryScheduleMode,
        groceryScheduleDate, setGroceryScheduleDate, handleGroceryConfirm, getMealImage
      }} />
      <DueAlertModal ctx={{
        showDueAlert, dueSchedules, currentDueIndex, handleDueAlertDismiss, handleDueAlertAdd
      }} />

      {/* Dietary Disclaimer */}
      <p className="text-[10px] text-gray-400 text-center px-4 pb-2 leading-snug">
        Meal suggestions are for informational purposes only. AI-powered filtering may not identify all allergens.
        Always verify ingredients if you have food allergies. <a href="/terms" className="underline">Terms</a>
      </p>
    </div>
  );
};

export default MealPlans;